/**
 * product-zoom.js  –  PhotoSwipe 5 zoom for wokiee theme-lightbox
 *
 * Loaded as <script type="module">.
 * Uses only photoswipe.min.js (ESM core) – no vendor bundle needed.
 *
 * Behaviour:
 *   - Intercepts lightbox-opener clicks (capture phase)
 *   - Collects images from the existing theme-lightbox HTML
 *   - Opens PhotoSwipe with click-to-zoom, pinch-to-zoom, wheel-zoom
 */

// ── PhotoSwipe lazy loader ────────────────────────────────────────────────────

let _PhotoSwipe = null;

async function getPhotoSwipe() {
  if (_PhotoSwipe) return _PhotoSwipe;
  const mod = await import(window.pswpCoreUrl);
  _PhotoSwipe = mod.default;
  return _PhotoSwipe;
}

// ── helpers ───────────────────────────────────────────────────────────────────

function getImages(lightboxEl) {
  return Array.from(lightboxEl.querySelectorAll('.theme-lightbox__image'));
}

function startIndexFor(images, mediaId) {
  if (!mediaId) return 0;
  const idx = images.findIndex(img => img.getAttribute('data-media-id') === mediaId);
  return idx >= 0 ? idx : 0;
}

function buildDataSource(images) {
  return images.map(img => {
    const w = img.naturalWidth  || parseInt(img.getAttribute('width'),  10) || 1200;
    const h = img.naturalHeight || parseInt(img.getAttribute('height'), 10) || 1200;
    return {
      src:    img.src,
      srcset: img.srcset || '',
      msrc:   img.currentSrc || img.src,
      width:  w,
      height: h,
      alt:    img.alt || '',
      element: img,           // thumbnail element for open animation
    };
  });
}

function bindProductGalleryFrames(pswp) {
  pswp.on('contentLoad', (e) => {
    const { content } = e;
    if (content.type !== 'image') return;

    e.preventDefault();

    const img = document.createElement('img');
    img.className = 'pswp__img';
    img.src = content.data.src;
    img.alt = content.data.alt || '';
    if (content.data.srcset) {
      img.srcset = content.data.srcset;
    }

    const frame = document.createElement('div');
    frame.className = 'product-pswp__frame';
    frame.appendChild(img);

    content.element = img;
    content.frameElement = frame;
    content.state = 'loading';

    if (img.complete) {
      content.onLoaded();
    } else {
      img.onload = () => content.onLoaded();
      img.onerror = () => content.onError();
    }
  });

  pswp.on('contentAppend', (e) => {
    const { content } = e;
    if (content.frameElement && !content.frameElement.parentNode) {
      e.preventDefault();
      content.slide.container.appendChild(content.frameElement);
    }
  });

  pswp.on('contentRemove', (e) => {
    const { content } = e;
    if (content.frameElement?.parentNode) {
      e.preventDefault();
      content.frameElement.remove();
    }
  });

  pswp.on('loadComplete', ({ content }) => {
    if (content.type !== 'image' || !content.frameElement || !content.slide?.container) return;
    if (!content.frameElement.parentNode) {
      content.slide.container.appendChild(content.frameElement);
    }
  });
}

// ── open PhotoSwipe ───────────────────────────────────────────────────────────

async function openPhotoSwipe(lightboxEl, mediaId) {
  const images = getImages(lightboxEl);
  if (!images.length) return;

  const PhotoSwipe = await getPhotoSwipe();

  const arrowPrev = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';
  const arrowNext = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';

  const pswp = new PhotoSwipe({
    dataSource: buildDataSource(images),
    index:          startIndexFor(images, mediaId),
    mainClass:      'pswp--product-gallery',

    // appearance
    bgOpacity:      1,

    // custom arrow icons
    arrowPrevSVG:   arrowPrev,
    arrowNextSVG:   arrowNext,

    // zoom / pan
    wheelToZoom:    true,     // mouse-wheel zooms
    allowMouseDrag: true,     // drag while zoomed in
    allowPanToNext: false,    // no accidental slide while panning

    // UI
    zoom:           false,    // hide zoom button (dbl-click / pinch still work)
    returnFocus:    true,

    // thumbnail animation – fly out from the clicked image
    thumbEl: (data) => data.element,
  });

  bindProductGalleryFrames(pswp);

  pswp.init();
}

// ── intercept clicks BEFORE native lightbox-opener handler ───────────────────

document.addEventListener('click', (e) => {
  if (!window.pswpCoreUrl) return;

  const opener = e.target.closest('lightbox-opener');
  if (!opener) return;

  // Stop the event reaching the custom-element's own click listener
  e.stopPropagation();

  const lightboxId = opener.getAttribute('data-id');
  const mediaId    = opener.getAttribute('data-media-id');
  const lightboxEl = lightboxId ? document.getElementById(lightboxId) : null;

  if (lightboxEl) {
    openPhotoSwipe(lightboxEl, mediaId);
  }
}, true /* capture phase */);
